/**
 * Formats a number as currency with configurable precision.
 * For DKK, the symbol "kr" is placed after the amount (e.g., "6.25 kr").
 * For other currencies, the symbol position is handled by Intl.NumberFormat (e.g., "$6.25").
 *
 * @param options.value - The numeric value to format
 * @param options.currency - Currency code (e.g., 'USD', 'EUR', 'DKK')
 * @param options.locale - Locale code (default: 'en-US')
 * @param options.minDigits - Minimum fraction digits (default: 2)
 * @param options.maxDigits - Maximum fraction digits (default: 2)
 * @returns Formatted currency string
 *
 * @example
 * currencyFormatter({ value: 1234.56, currency: 'USD' })           // "$1,234.56"
 * currencyFormatter({ value: 1234.56, currency: 'USD', minDigits: 0, maxDigits: 0 })    // "$1,235"
 * currencyFormatter({ value: 1234.56, currency: 'EUR', minDigits: 0, maxDigits: 2 })    // "€1,234.56"
 * currencyFormatter({ value: 6.25, currency: 'DKK' })      // "6.25 kr"
 */
export const currencyFormatter = ({
  value,
  currency,
  locale = 'en-US',
  minDigits = 2,
  maxDigits = 2,
}: {
  value: number;
  currency: string;
  locale?: string;
  minDigits?: number;
  maxDigits?: number;
}) => {
  const currencyUpper = (currency || 'USD').toUpperCase();

  // For DKK, format number separately and place "kr" after the amount.
  // This exception is necessary because:
  // 1. Intl.NumberFormat with 'en-US' locale formats DKK as "DKK 6.25" (currency code before amount)
  // 2. The Ruby Money gem (used in CurrencyFormatter.format) formats DKK as "6.25 kr" (symbol after amount)
  // 3. Danish convention places "kr" after the amount, matching the Money gem's behavior
  // We maintain consistency with the backend Money gem formatting to ensure frontend and backend display the same format.
  if (currencyUpper === 'DKK') {
    const numberFormatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
    }).format(value);
    return `${numberFormatted} kr`;
  }

  // For other currencies, use Intl.NumberFormat which handles symbol positioning automatically
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyUpper,
    // These options are needed to round to whole numbers.
    minimumFractionDigits: minDigits, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: maxDigits, // (causes 2500.99 to be printed as $2,501)
  });

  return formatter.format(value);
};
